import { inject, DOCUMENT } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { AuthStore } from './auth.store';
import { isReallyOnline } from '@wheere-front/shared-util';

/**
 * V7 platform-admin guard. Replaces the legacy `userIsSuperAdmin` (the SUPERADMIN role was
 * dropped in V7 along with WHEERE_MAINTAINER). PLATFORM_ADMIN is the canonical bypass.
 */
export function userIsPlatformAdmin(redirectUrl = '/forbidden'): CanActivateFn {
  return () => {
    if (inject(AuthStore).isPlatformAdmin()) {
      return true;
    }
    return inject(Router).parseUrl(redirectUrl);
  };
}

/** @deprecated V7 — use {@link userIsPlatformAdmin}. Kept as a one-line alias for any straggler. */
export const userIsSuperAdmin = userIsPlatformAdmin;

export function userHasAnyRole(roles: string | string[], redirectUrl = '/forbidden'): CanActivateFn {
  return async () => {
    const router = inject(Router);
    const authStore = inject(AuthStore);

    // Check true online status via ping
    const online = await isReallyOnline();
    // If truly offline => allow
    if (!online) {
      return true;
    }
    const hasAnyRoleNow = authStore.hasAnyRole(roles);
    if (hasAnyRoleNow) {
      return true;
    }

    // Roles may still be loading right after login. Wait briefly for them to populate.
    const waitFor = (predicate: () => boolean, timeoutMs = 1200, intervalMs = 60) =>
      new Promise<boolean>(resolve => {
        const started = Date.now();
        const i = setInterval(() => {
          if (predicate() || Date.now() - started >= timeoutMs) {
            clearInterval(i);
            resolve(predicate());
          }
        }, intervalMs);
      });

    const satisfied = await waitFor(() => authStore.hasAnyRole(roles));
    if (satisfied) return true;

    // Otherwise, redirect
    return router.parseUrl(redirectUrl);
  };
}

/**
 * Bounce an ORG_CUSTOMER end-client to the customer-facing app and prevent
 * them from ever rendering the integrator (or any other non-customer) shell.
 *
 * <p>Today every non-customer route is already protected by
 * {@link userHasAnyRole} with an `ORG_CUSTOMER`-free allow-list, so the user
 * would otherwise land on `/forbidden`. That's functionally safe but ugly —
 * they get a dead end inside an app they can't use. This guard intercepts
 * them at the root and redirects to `customer.<env>.wheere.tech` (or the
 * passed override).
 *
 * <p>Subdomain swap rule for the prod-ish envs:
 * `integrator.dev.wheere.tech` → `customer.dev.wheere.tech`
 * `integrator.qualif.wheere.tech` → `customer.qualif.wheere.tech`
 * `integrator.wheere.tech` → `customer.wheere.tech`
 * For localhost we fall back to the dev port the customer app pins
 * (`:4201`) since there's no subdomain to swap.
 *
 * <p>PLATFORM_* and any of the ORG_ADMIN / ORG_OPERATOR / ORG_VIEWER roles
 * are NOT bounced — a multi-role user (rare; the team page hands out one
 * role) keeps integrator access. Only the `ORG_CUSTOMER`-only case bounces.
 *
 * @param customerHomeUrl Optional absolute URL override. When omitted the
 *   guard derives it from `window.location.host` using the subdomain swap.
 */
export function bounceCustomerToCustomerApp(customerHomeUrl?: string): CanActivateFn {
  return async () => {
    const authStore = inject(AuthStore);
    const documentWindow = inject(DOCUMENT).defaultView;

    // Same offline tolerance as the other guards: don't bounce on a flaky
    // network — let the cached SPA render and the user see their data.
    const online = await isReallyOnline();
    if (!online) return true;

    const roles = authStore.roles();
    // Wait briefly for roles to populate post-login.
    if (roles.length === 0) {
      const waited = await new Promise<string[]>(resolve => {
        const started = Date.now();
        const i = setInterval(() => {
          const r = authStore.roles();
          if (r.length > 0 || Date.now() - started >= 1200) {
            clearInterval(i);
            resolve(r);
          }
        }, 60);
      });
      if (waited.length === 0) return true; // unknown roles — let downstream guards handle it
    }

    const current = authStore.roles();
    const isCustomerOnly =
      current.includes('ORG_CUSTOMER') &&
      !current.some(r => ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'ORG_ADMIN', 'ORG_OPERATOR', 'ORG_VIEWER'].includes(r));
    if (!isCustomerOnly) return true;

    const target = customerHomeUrl ?? deriveCustomerUrl(documentWindow?.location);
    if (target && documentWindow) {
      documentWindow.location.replace(target);
    }
    // Block the navigation either way — the redirect is async at the
    // browser level; returning false stops the Angular router from
    // rendering the integrator shell in the meantime.
    return false;
  };
}

/** Best-effort host-to-customer-host swap. Returns null when we can't infer. */
function deriveCustomerUrl(loc: Location | undefined): string | null {
  if (!loc) return null;
  const host = loc.host; // e.g. "integrator.dev.wheere.tech" or "localhost:4202"
  if (host.startsWith('integrator.')) {
    return `${loc.protocol}//${host.replace(/^integrator\./, 'customer.')}/`;
  }
  if (host.startsWith('inside.')) {
    return `${loc.protocol}//${host.replace(/^inside\./, 'customer.')}/`;
  }
  if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) {
    // Customer dev-server pins :4201.
    return `${loc.protocol}//${host.split(':')[0]}:4201/`;
  }
  return null;
}

/**
 * Bounce any ORG_* user (ORG_ADMIN / ORG_OPERATOR / ORG_VIEWER / ORG_CUSTOMER)
 * away from the `wheere-inside` admin app and into `wheere-integrator`. From
 * there the existing {@link bounceCustomerToCustomerApp} guard cascades
 * ORG_CUSTOMER on to the customer app — net result:
 *  - PLATFORM_ADMIN / PLATFORM_SUPPORT → stay in inside (their normal home)
 *  - ORG_ADMIN / ORG_OPERATOR / ORG_VIEWER → integrator
 *  - ORG_CUSTOMER → integrator → customer (two-hop, transparent)
 *
 * <p>Subdomain swap:
 * `inside.dev.wheere.tech` → `integrator.dev.wheere.tech`
 * `inside.qualif.wheere.tech` → `integrator.qualif.wheere.tech`
 * `inside.wheere.tech` → `integrator.wheere.tech`
 * Local: `localhost:4200` → `localhost:4202`.
 *
 * @param integratorHomeUrl Optional absolute URL override.
 */
export function bounceOrgToIntegratorApp(integratorHomeUrl?: string): CanActivateFn {
  return async () => {
    const authStore = inject(AuthStore);
    const documentWindow = inject(DOCUMENT).defaultView;

    const online = await isReallyOnline();
    if (!online) return true;

    let current = authStore.roles();
    if (current.length === 0) {
      current = await new Promise<string[]>(resolve => {
        const started = Date.now();
        const i = setInterval(() => {
          const r = authStore.roles();
          if (r.length > 0 || Date.now() - started >= 1200) {
            clearInterval(i);
            resolve(r);
          }
        }, 60);
      });
      if (current.length === 0) return true;
    }

    const PLATFORM = ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'];
    const ORG = ['ORG_ADMIN', 'ORG_OPERATOR', 'ORG_VIEWER', 'ORG_CUSTOMER'];
    const isOrgOnly = current.some(r => ORG.includes(r)) && !current.some(r => PLATFORM.includes(r));
    if (!isOrgOnly) return true;

    const target = integratorHomeUrl ?? deriveIntegratorUrl(documentWindow?.location);
    if (target && documentWindow) {
      documentWindow.location.replace(target);
    }
    return false;
  };
}

/** Best-effort host-to-integrator-host swap. Returns null when we can't infer. */
function deriveIntegratorUrl(loc: Location | undefined): string | null {
  if (!loc) return null;
  const host = loc.host;
  if (host.startsWith('inside.')) {
    return `${loc.protocol}//${host.replace(/^inside\./, 'integrator.')}/`;
  }
  if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) {
    // Integrator dev-server runs on :4202.
    return `${loc.protocol}//${host.split(':')[0]}:4202/`;
  }
  return null;
}

/**
 * Guard 3: authenticationGuard
 * If offline => allow (true).
 * If online => check if logged in; else do Keycloak login.
 */
export function authenticationGuard(): CanActivateFn {
  return async (_, state: RouterStateSnapshot) => {
    const service = inject(KeycloakService);
    const documentWindow = inject(DOCUMENT).defaultView;

    // Check true online status via ping
    const online = await isReallyOnline();
    if (!online) {
      // If truly offline => allow the route
      return true;
    }

    // If online => check if user is logged in
    const loggedIn = service.isLoggedIn();
    if (loggedIn) {
      if (loggedIn) {
        return true;
      }
      return true;
    }

    // If not logged in => initiate login
    await service.login({
      redirectUri: documentWindow?.location.origin + state.url,
      scope: 'openid offline_access',
    });

    // The guard must return false or a UrlTree to block navigation
    return false;
  };
}
